import type { VinDetail } from '@packages/models/api/techline';

import { Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

import { CollapsibleSection } from './collapsible-section';

const useStyles = createUseStyles({
    vehicleInfoContainerItem: {
        flex: 1,
    },
});

/**
 * Props for the {@link VehicleInfoSection} component.
 */
export type VehicleInfoSectionProps = {
    /** VIN decode details for the vehicle associated with the case. */
    vehicleVinData?: VinDetail;
};

/**
 * Displays vehicle information (VIN, engine number, model year, carline, etc.) for a techline
 * case. Wraps itself in a {@link CollapsibleSection} so callers do not need to provide an
 * external section wrapper.
 *
 * @param props - {@link VehicleInfoSectionProps}
 * @returns the vehicle info section JSX
 */
export function VehicleInfoSection({ vehicleVinData }: VehicleInfoSectionProps) {
    const classes = useStyles();

    return (
        <CollapsibleSection defaultExpanded={true} showDividerTop title="Vehicle Information">
            <div className="d-flex flex-row">
                <div className={`d-flex flex-column ${classes.vehicleInfoContainerItem}`}>
                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">VIN</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.vin || '--'}</Typography>
                        </div>
                    </div>

                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">Engine number</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.engineId || '--'}</Typography>
                        </div>
                    </div>

                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">Color</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.colorCategory || '--'}</Typography>
                        </div>
                    </div>

                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">Model option</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.optionCode || '--'}</Typography>
                        </div>
                    </div>
                </div>

                <div className={`d-flex flex-column ${classes.vehicleInfoContainerItem}`}>
                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">Model year</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.modelYear || '--'}</Typography>
                        </div>
                    </div>

                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">Carline</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.model || '--'}</Typography>
                        </div>
                    </div>

                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">Transmission number</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.vehicleTransmissionNumber || '--'}</Typography>
                        </div>
                    </div>

                    <div className="d-flex flex-column mb-5">
                        <Typography variant="label">Model</Typography>
                        <div className="mt-1">
                            <Typography>{vehicleVinData?.modelNumber || '--'}</Typography>
                        </div>
                    </div>
                </div>
            </div>
        </CollapsibleSection>
    );
}
